import {useEffect} from "react";
import {useField, useFormikContext} from "formik";
import {
    defaultValidation, validationSchemaAchUSD,
    validationSchemaDomesticEUR,
    validationSchemaDomesticGBP, validationSchemaFedwireUSD,
    validationSchemaInternationalEUR, validationSchemaInternationalGBP, validationSchemaSwiftUSD
} from "./ValidationSchemas";
import * as yup from "yup";
import useDebounce from "../../../hooks/useDebounce";
import {getBicCodeThunk} from "../../../store/reducers/ActionCreators";
import {useAppDispatch, useAppSelector} from "../../../hooks/redux";
import {clearFormByChangeType} from "../../../helpers/clearFormHelper";

export const CurrencyHelper = (props: any) => {


    useEffect(() => {

        props.accountsList.map((account: any) => {
            if (account.number === props.hiddenAccNumber) {
                props.setCurrency(account.currency)
                props.setAccountNumber(account.number)
            }
        })
    }, [props.accountsList, props.payerAccount, props.hiddenAccNumber])

    useEffect(() => {
        props.setPaymentRegion(props.values.paymentRegions)
    }, [props.values.paymentRegions])

    return null
}

export const PaymentTypeHelper = (props: any) => {

    const { values } = useFormikContext();
    const [field, meta, helpers] = useField<string>(props.name);
    const { setValue } = helpers;

    useEffect(() => {
        setValue("")
        // @ts-ignore
    },[values.recipientsBankCountry])

    return null
}

export const amountValidation = yup.string()
    .matches(/^[-+]?[0-9]*[.]?[0-9]+(?:[eE][-+]?[0-9]+)?$/, "Must be only digits")
    .required('Required field')
    .max(10, 'max 10 digits')
    .test(
        'no-leading-zero',
        'invalid format',
        (value, context) => {

            const tempArr = context.originalValue.split('')

            const onlyZero = tempArr.every((item: string) => {
                if (item === '0') {
                    return true
                }
            })

            if (context.originalValue && onlyZero) {
                return false
            } else if ((context.originalValue.match(/,/g) || []).length > 1) {
                return false
            }
            return true
        }
    )
    .test(
        'max-decimal-places',
        'max 2 symbols after .',
        (value) => {
            const parts = value.split('.')
            return parts.length === 1 || parts[1].length <= 2
        }
    )
    .test(
    'positive-number',
    'Value must be positive',
    (value) => {
        const num = parseFloat(value);
        return !isNaN(num) && num > 0;
    }
)

// Function that returns the validation schema based on the payment region
export const getValidationSchema = (paymentRegion: string, currency? : string) => {

    const comparedValidationSchemaInternationalEUR = yup.object({
        name: validationSchemaInternationalEUR.name,
        surname: validationSchemaInternationalEUR.surname,
        companyName: validationSchemaInternationalEUR.companyName,
        recipientsIban: validationSchemaInternationalEUR.recipientsIban,
        recipientsSwiftBic: validationSchemaInternationalEUR.recipientsSwiftBic,
        recipientsAddress: validationSchemaInternationalEUR.recipientsAddress,
        recipientCity: validationSchemaInternationalEUR.recipientCity,
        recipientPostCode: validationSchemaInternationalEUR.recipientPostCode,
        recipientCountry: validationSchemaInternationalEUR.recipientCountry,
        bankName: validationSchemaInternationalEUR.bankName,
        bankAddress: validationSchemaInternationalEUR.bankAddress,
        bankCity: validationSchemaInternationalEUR.bankCity,
        bankPostCode: validationSchemaInternationalEUR.bankPostCode,
        bankCountry: validationSchemaInternationalEUR.bankCountry,
        reference: validationSchemaInternationalEUR.reference,
        payerAccount: validationSchemaInternationalEUR.payerAccount,
        paymentRegions: validationSchemaInternationalEUR.paymentRegions,
        templateName: validationSchemaInternationalEUR.templateName,
        amount: amountValidation
    });

    const comparedValidationSchemaDomesticEUR = yup.object({
        name: validationSchemaDomesticEUR.name,
        surname: validationSchemaDomesticEUR.surname,
        recipientsIban: validationSchemaDomesticEUR.recipientsIban,
        payerAccount: validationSchemaDomesticEUR.payerAccount,
        paymentRegions: validationSchemaDomesticEUR.paymentRegions,
        companyName: validationSchemaDomesticEUR.companyName,
        recipientsSwiftBic: validationSchemaDomesticEUR.recipientsSwiftBic,
        reference: validationSchemaDomesticEUR.reference,
        templateName: validationSchemaDomesticEUR.templateName,
        amount: amountValidation
    });

    const comparedValidationSchemaDomesticGBP = yup.object({
        payerAccount: validationSchemaDomesticGBP.payerAccount,
        name: validationSchemaDomesticGBP.name,
        surname: validationSchemaDomesticGBP.surname,
        companyName: validationSchemaDomesticGBP.companyName,
        reference: validationSchemaDomesticGBP.reference,
        recipientsAccountNumber: validationSchemaDomesticGBP.recipientsAccountNumber,
        sortCode: validationSchemaDomesticGBP.sortCode,
        recipientsIban: validationSchemaDomesticGBP.recipientsIban,
        recipientsSwiftBic: validationSchemaDomesticGBP.recipientsSwiftBic,
        paymentRegions: validationSchemaDomesticGBP.paymentRegions,
        templateName: validationSchemaDomesticGBP.templateName,
        amount: amountValidation
    });

    const comparedValidationSchemaInternationalGBP = yup.object({
        name: validationSchemaInternationalGBP.name,
        surname: validationSchemaInternationalGBP.surname,
        companyName: validationSchemaInternationalGBP.companyName,
        recipientsIban: validationSchemaInternationalGBP.recipientsIban,
        recipientsSwiftBic: validationSchemaInternationalGBP.recipientsSwiftBic,
        recipientsAddress: validationSchemaInternationalGBP.recipientsAddress,
        recipientCity: validationSchemaInternationalGBP.recipientCity,
        recipientPostCode: validationSchemaInternationalGBP.recipientPostCode,
        recipientCountry: validationSchemaInternationalGBP.recipientCountry,
        bankName: validationSchemaInternationalGBP.bankName,
        bankAddress: validationSchemaInternationalGBP.bankAddress,
        bankCity: validationSchemaInternationalGBP.bankCity,
        bankPostCode: validationSchemaInternationalGBP.bankPostCode,
        bankCountry: validationSchemaInternationalGBP.bankCountry,
        reference: validationSchemaInternationalGBP.reference,
        payerAccount: validationSchemaInternationalGBP.payerAccount,
        paymentRegions: validationSchemaInternationalGBP.paymentRegions,
        templateName: validationSchemaInternationalGBP.templateName,
        recipientsAccountNumber: validationSchemaInternationalGBP.recipientsAccountNumber,
        sortCode: validationSchemaInternationalGBP.sortCode,
        amount: amountValidation
    });

    // const comparedValidationSchemaAchUSD = yup.object({
    //     name: validationSchemaAchUSD.name,
    //     surname: validationSchemaAchUSD.surname,
    //     companyName: validationSchemaAchUSD.companyName,
    //     bankName: validationSchemaAchUSD.bankName,
    //     reference: validationSchemaAchUSD.reference,
    //     recipientsAccountNumber: validationSchemaAchUSD.recipientsAccountNumber,
    //     achRoutingNumber: validationSchemaAchUSD.achRoutingNumber,
    //     paymentRegions: validationSchemaAchUSD.paymentRegions,
    //     templateName: validationSchemaAchUSD.templateName,
    //     amount: amountValidation
    // });

    // const comparedValidationSchemaSwiftUSD = yup.object({
    //     name: validationSchemaInternationalEUR.name,
    //     surname: validationSchemaInternationalEUR.surname,
    //     companyName: validationSchemaInternationalEUR.companyName,
    //     recipientsIban: validationSchemaInternationalEUR.recipientsIban,
    //     recipientsSwiftBic: validationSchemaInternationalEUR.recipientsSwiftBic,
    //     recipientsAddress: validationSchemaInternationalEUR.recipientsAddress,
    //     recipientCity: validationSchemaInternationalEUR.recipientCity,
    //     recipientPostCode: validationSchemaInternationalEUR.recipientPostCode,
    //     recipientCountry: validationSchemaInternationalEUR.recipientCountry,
    //     bankName: validationSchemaInternationalEUR.bankName,
    //     bankAddress: validationSchemaInternationalEUR.bankAddress,
    //     bankCity: validationSchemaInternationalEUR.bankCity,
    //     bankPostCode: validationSchemaInternationalEUR.bankPostCode,
    //     bankCountry: validationSchemaInternationalEUR.bankCountry,
    //     reference: validationSchemaInternationalEUR.reference,
    //     payerAccount: validationSchemaInternationalEUR.payerAccount,
    //     paymentRegions: validationSchemaInternationalEUR.paymentRegions,
    //     templateName: validationSchemaInternationalEUR.templateName,
    //     amount: amountValidation
    // });
    //
    // const comparedValidationSchemaFedwireUSD = yup.object({
    //     name: validationSchemaFedwireUSD.name,
    //     surname: validationSchemaFedwireUSD.surname,
    //     companyName: validationSchemaFedwireUSD.companyName,
    //     fedwireRoutingNumber: validationSchemaFedwireUSD.fedwireRoutingNumber,
    //     recipientsAccountNumber: validationSchemaFedwireUSD.recipientsAccountNumber,
    //     bankAddress: validationSchemaFedwireUSD.bankAddress,
    //     reference: validationSchemaFedwireUSD.reference,
    //     amount: amountValidation
    // });

    if (currency === "EUR") {
        switch (paymentRegion) {
            case "domestic":
                return comparedValidationSchemaDomesticEUR;
            case "international":
                return comparedValidationSchemaInternationalEUR;
        }
        return
    }

    if (currency === "GBP") {
        switch (paymentRegion) {
            case "domestic":
                return comparedValidationSchemaDomesticGBP;
            case "international":
                return comparedValidationSchemaInternationalGBP;
        }
        return
    }

    // if (currency === "USD") {
    //     switch (paymentRegion) {
    //         case "ACH":
    //             return comparedValidationSchemaAchUSD;
    //         case "SWIFT":
    //             return comparedValidationSchemaSwiftUSD;
    //         case "Fedwire":
    //             return comparedValidationSchemaFedwireUSD;
    //     }
    //     return
    // }

    return defaultValidation
}

export const SwiftCheckerHelper = (props: any) => {

    const dispatch = useAppDispatch()
    const token = useAppSelector(state => state.userReducer.userToken)
    const { values }: any = useFormikContext();
    const debouncedValue = useDebounce(values.recipientsIban, 2000);

    useEffect(() => {
        if (debouncedValue) {

            dispatch(getBicCodeThunk(debouncedValue, token))
                .then((res) => {
                    if (values.paymentRegions === "international" && props.currency === "EUR" && props.isAdditionalFields) {
                        props.setFieldValue("bankName", res.data.data.swift.bank.name)
                        props.setFieldValue("recipientsSwiftBic", res.data.data.swift.id)
                        props.setFieldValue("bankCity", res.data.data.swift.city.name)
                        props.setFieldValue("bankAddress", res.data.data.swift.address)
                        props.setFieldValue("bankPostCode", res.data.data.swift.postcode)
                        props.setFieldValue("bankCountry", res.data.data.swift.country.name)
                    }

                    if (values.paymentRegions === "domestic" && props.currency === "EUR" && props.isAdditionalFields) {
                        props.setFieldValue("recipientsSwiftBic", res.data.data.swift.id)
                    }

                    if (values.paymentRegions === "international" && props.currency === "GBP" && props.isAdditionalFields) {
                        props.setFieldValue("bankName", res.data.data.swift.bank.name)
                        props.setFieldValue("recipientsSwiftBic", res.data.data.swift.id)
                        props.setFieldValue("bankCity", res.data.data.swift.city.name)
                        props.setFieldValue("bankAddress", res.data.data.swift.address)
                        props.setFieldValue("bankPostCode", res.data.data.swift.postcode)
                        props.setFieldValue("bankCountry", res.data.data.swift.country.name)
                    }

                    if (values.paymentRegions === "domestic" && props.currency === "GBP" && props.isAdditionalFields) {
                        props.setFieldValue("recipientsSwiftBic", res.data.data.swift.id)
                    }

                    if (values.paymentRegions === "SWIFT" && props.currency === "USD" && props.isAdditionalFields) {
                        props.setFieldValue("bankName", res.data.data.swift.bank.name)
                        props.setFieldValue("recipientsSwiftBic", res.data.data.swift.id)
                        props.setFieldValue("bankCity", res.data.data.swift.city.name)
                        props.setFieldValue("bankAddress", res.data.data.swift.address)
                        props.setFieldValue("bankPostCode", res.data.data.swift.postcode)
                        props.setFieldValue("bankCountry", res.data.data.swift.country.name)
                    }

                    props.setIsIbanWrong(false)
                })
                .catch((e) => {
                    console.log(e)
                    if (e.response.data === "Invalid iban" || e.response.data === 'IBAN not found') {
                        props.setIsIbanWrong(true)
                    }
                })

        } else if (debouncedValue === '') {
            props.setIsIbanWrong(false)
        }
        // @ts-ignore
    },[debouncedValue])

    return null
}

export const ResetFormHelper = (props: any) => {

    const { values, setValues }: any = useFormikContext();

    useEffect(() => {
        clearFormByChangeType(values, setValues, ["payerAccount", "recipientType", "type"])
    }, [props.accountNumber]);

    return null
}
